import { ChatCircleIcon } from "@phosphor-icons/react/dist/csr/ChatCircle";
import { ChatTextIcon } from "@phosphor-icons/react/dist/csr/ChatText";
import { DotsThreeIcon } from "@phosphor-icons/react/dist/csr/DotsThree";
import { DownloadSimpleIcon } from "@phosphor-icons/react/dist/csr/DownloadSimple";
import { LockIcon } from "@phosphor-icons/react/dist/csr/Lock";
import { NotebookIcon } from "@phosphor-icons/react/dist/csr/Notebook";
import { NotePencilIcon } from "@phosphor-icons/react/dist/csr/NotePencil";
import { useState } from "react";
import { Button } from "@volca/ui/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@volca/ui/components/ui/dropdown-menu";
import { Spinner } from "@volca/ui/components/ui/spinner";
import type { PayPeriodLockTarget } from "@/payroll/pay-periods/pay-period-lock";
import { PayPeriodLockDialog } from "@/payroll/pay-periods/pay-period-lock-action";
import {
  isPayBreakdownRerunMenuActionVisible,
  type PayBreakdownRerunMenuAction,
  PayBreakdownRerunMenuItem,
} from "./pay-breakdown-rerun-action";
import type {
  PayBreakdownDownloadAction,
  PayBreakdownNextPeriodNoteAction,
} from "./types";

interface PayBreakdownMoreActionsMenuProps {
  nextPeriodNote?: PayBreakdownNextPeriodNoteAction;
  download?: PayBreakdownDownloadAction;
  onOpenManualEdits?: () => void;
  onOpenComments?: () => void;
  onOpenExplanationChat?: () => void;
  rerun?: PayBreakdownRerunMenuAction;
  // Closing out the period. Terminal, once per period, and only Volca can undo
  // it — so it sits with the other occasional period actions rather than
  // holding a button in the header.
  lock?: PayPeriodLockTarget;
}

export const PayBreakdownMoreActionsMenu = ({
  nextPeriodNote,
  download,
  onOpenManualEdits,
  onOpenComments,
  onOpenExplanationChat,
  rerun,
  lock,
}: PayBreakdownMoreActionsMenuProps) => {
  const [isLockDialogOpen, setIsLockDialogOpen] = useState(false);
  const hasRerun = isPayBreakdownRerunMenuActionVisible(rerun);
  const busy = Boolean(download?.isDownloading || rerun?.action.isRunning);
  const hasPanels =
    !!onOpenManualEdits || !!onOpenComments || !!onOpenExplanationChat;
  const hasOtherActions = !!download || hasPanels || hasRerun || !!lock;
  const hasActions = hasOtherActions || !!nextPeriodNote;

  if (!hasActions) return null;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger
          render={
            <Button
              variant="secondary"
              size="icon"
              aria-label="More actions"
              loading={busy}
              loadingLabel="More actions, in progress"
            />
          }
        >
          <DotsThreeIcon className="h-4 w-4" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-56">
          {nextPeriodNote && (
            <DropdownMenuItem onClick={nextPeriodNote.onOpen}>
              <NotebookIcon className="h-4 w-4" />
              Note for next open period
            </DropdownMenuItem>
          )}
          {nextPeriodNote && hasOtherActions && <DropdownMenuSeparator />}
          {download && (
            <DropdownMenuItem disabled={busy} onClick={download.onDownload}>
              {download.isDownloading ? (
                <Spinner aria-hidden />
              ) : (
                <DownloadSimpleIcon className="h-4 w-4" />
              )}
              Download XLSX
            </DropdownMenuItem>
          )}
          {download && hasRerun && <DropdownMenuSeparator />}
          {rerun && <PayBreakdownRerunMenuItem rerun={rerun} busy={busy} />}
          {hasPanels && (download || hasRerun) && <DropdownMenuSeparator />}
          {onOpenExplanationChat && (
            <DropdownMenuItem disabled={busy} onClick={onOpenExplanationChat}>
              <ChatCircleIcon className="h-4 w-4" />
              AI chat
            </DropdownMenuItem>
          )}
          {onOpenManualEdits && (
            <DropdownMenuItem disabled={busy} onClick={onOpenManualEdits}>
              <NotePencilIcon className="h-4 w-4" />
              Manual edits
            </DropdownMenuItem>
          )}
          {onOpenComments && (
            <DropdownMenuItem disabled={busy} onClick={onOpenComments}>
              <ChatTextIcon className="h-4 w-4" />
              Comments
            </DropdownMenuItem>
          )}
          {lock && (download || hasRerun || hasPanels) && (
            <DropdownMenuSeparator />
          )}
          {lock && (
            <DropdownMenuItem
              disabled={busy}
              onClick={() => setIsLockDialogOpen(true)}
            >
              <LockIcon className="h-4 w-4" />
              Lock pay period
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      {/* Outside the dropdown on purpose: the menu unmounts its content on
          select, which would take the dialog with it. */}
      {lock && (
        <PayPeriodLockDialog
          businessId={lock.businessId}
          period={lock.period}
          open={isLockDialogOpen}
          onOpenChange={setIsLockDialogOpen}
        />
      )}
      <span role="status" className="sr-only">
        {download?.isDownloading
          ? "Download XLSX in progress"
          : rerun?.action.isRunning
            ? "Rerun payroll in progress"
            : ""}
      </span>
    </>
  );
};
